/**
 * The qbo_connect callback token's reader (F11).
 *
 * The OAuth callback (api/src/functions/qboIntegration.ts) ends every
 * outcome as a fact token in a redirect to '/?qbo_connect=<token>' —
 * 'connected', or one of thirteen failure classes (checkQboCallback's
 * state_missing / state_malformed / state_invalid / state_not_yet_valid
 * / state_expired / user_denied / provider_error / code_missing /
 * realm_missing / realm_invalid, plus the handler's exchange_failed,
 * connect_failed, and another_company_connected — the 2026-08-31
 * second-company refusal). The root route's <Navigate replace> then strips the
 * query on the way to /hub, so without a capture BEFORE that redirect
 * the token is gone: an owner who declined consent at Intuit landed on
 * the hub with no sign anything happened.
 *
 * So the root redirect calls captureQboConnectToken with the landing
 * search string, the token rides sessionStorage across the redirect,
 * and QboConnectResultBanner reads it on the billing surface where the
 * QuickBooks card (and the retry) lives.
 *
 * SCOPE: originally failure tokens only (the sweep's UX-boundary
 * ruling deferred the success design). That deferral ended with the
 * 2026-08-31 UX pass: 'connected' now renders its own calm banner,
 * naming the company via the status read the QuickBooks card already
 * makes.
 */

import { contactSentence, type SupportContact } from './errorCopy';

const STORAGE_KEY = 'qbo_connect_result';

/** Pull qbo_connect out of a search string and remember it. Called by
 *  the root redirect, synchronously, before <Navigate> strips the
 *  query. Storage failures (private windows, blocked site data) drop
 *  the token rather than the landing. */
export function captureQboConnectToken(search: string): void {
  let token: string | null;
  try {
    token = new URLSearchParams(search).get('qbo_connect');
  } catch {
    return;
  }
  if (token == null || token === '') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Nothing to do: the redirect must still happen.
  }
}

export function readQboConnectToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearQboConnectToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // A token that cannot be cleared was never stored either.
  }
}

/**
 * One honest sentence per failure token. Facts only, epistemics
 * respected: every token minted BEFORE saveTokens ran may say nothing
 * was connected; connect_failed is the outer catch and cannot know
 * (saveTokens itself may have thrown mid-write), so it points at the
 * connection status instead of asserting.
 */
const FAILURE_SENTENCES: Readonly<Record<string, string>> = {
  user_denied:
    "QuickBooks wasn't connected because access was declined on the QuickBooks screen. Press Connect QuickBooks again if that was a mistake.",
  state_expired:
    'The QuickBooks connection took too long, so nothing was connected. Press Connect QuickBooks to start again.',
  state_missing:
    "The QuickBooks connection couldn't be confirmed, so nothing was connected. Press Connect QuickBooks to start again.",
  state_malformed:
    "The QuickBooks connection couldn't be confirmed, so nothing was connected. Press Connect QuickBooks to start again.",
  state_invalid:
    "The QuickBooks connection couldn't be confirmed, so nothing was connected. Press Connect QuickBooks to start again.",
  state_not_yet_valid:
    "The QuickBooks connection couldn't be confirmed, so nothing was connected. Press Connect QuickBooks to start again.",
  provider_error:
    'QuickBooks had a problem while connecting, so nothing was connected. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.',
  code_missing:
    "QuickBooks didn't send back everything needed, so nothing was connected. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.",
  realm_missing:
    "QuickBooks didn't send back everything needed, so nothing was connected. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.",
  realm_invalid:
    "QuickBooks didn't send back everything needed, so nothing was connected. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.",
  exchange_failed:
    "The QuickBooks connection didn't finish, so nothing was connected. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.",
  connect_failed:
    'The QuickBooks connection may not have finished. If this page shows Not connected, press Connect QuickBooks again.',
  // The 2026-08-31 second-company refusal. This static sentence is the
  // NAMELESS fallback; the banner upgrades it with the connected
  // company's stored name (qboAnotherCompanySentence below) when the
  // status read holds one. Epistemically safe: the refusal happens
  // BEFORE the token exchange, so "nothing changed" is a provable fact.
  another_company_connected:
    'Another QuickBooks company is already connected, so nothing changed. To switch companies, disconnect the current one first.',
};

/** The second-company refusal's sentence, with the connected company
 *  named when the status read knows it. Null (or blank) name falls back
 *  to the static nameless sentence above. */
export function qboAnotherCompanySentence(companyName: string | null): string {
  if (companyName === null || companyName.trim() === '') {
    return FAILURE_SENTENCES['another_company_connected']!;
  }
  return `${companyName} is already connected to QuickBooks, so nothing changed. To switch companies, disconnect the current one first.`;
}

/** The sentence for a failure token; a token nobody has ruled on gets a
 *  generic sentence that NAMES the token, so a new callback class is
 *  visible rather than blank. */
export function qboConnectFailureSentence(token: string, contact: SupportContact): string {
  const own = FAILURE_SENTENCES[token];
  if (own !== undefined) return contactSentence(own, contact);
  return contactSentence(
    "The QuickBooks connection didn't finish. Press Connect QuickBooks again, and if it fails again, tell {contact_billing}.",
    contact,
  );
}
